import { useState, useEffect, useCallback, useMemo } from 'react';
import SIDE_PANEL_WIDGETS from '../widgets/sidePanelWidgetRegistry.js';

const STORAGE_KEY = 'astrantis_sidepanel_widgets';

const WIDGETS_BY_ID = SIDE_PANEL_WIDGETS.reduce((acc, w) => {
    acc[w.id] = w;
    return acc;
}, {});

function defaultPrefs() {
    return {
        order: SIDE_PANEL_WIDGETS.map(w => w.id),
        enabled: SIDE_PANEL_WIDGETS.reduce((acc, w) => {
            acc[w.id] = w.defaultEnabled;
            return acc;
        }, {}),
    };
}

/**
 * 레지스트리에 정의된 위젯 id 기준으로 저장된 설정을 보정한다.
 * - 레지스트리에서 제거된 id는 버리고, 새로 추가된 id는 order 끝에 append한다.
 */
function reconcile(saved) {
    const fallback = defaultPrefs();
    const knownIds = new Set(SIDE_PANEL_WIDGETS.map(w => w.id));

    const order = (Array.isArray(saved.order) ? saved.order : [])
        .filter(id => knownIds.has(id));
    SIDE_PANEL_WIDGETS.forEach(w => {
        if (!order.includes(w.id)) order.push(w.id);
    });

    const enabled = { ...fallback.enabled };
    if (saved.enabled && typeof saved.enabled === 'object') {
        Object.keys(saved.enabled).forEach(id => {
            if (knownIds.has(id)) enabled[id] = !!saved.enabled[id];
        });
    }
    // removable: false인 위젯은 항상 켜짐 상태 유지
    SIDE_PANEL_WIDGETS.forEach(w => {
        if (w.removable === false) enabled[w.id] = true;
    });

    return { order, enabled };
}

function loadPrefs() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return defaultPrefs();
        return reconcile(JSON.parse(raw));
    } catch {
        return defaultPrefs();
    }
}

/**
 * 사이드 패널 위젯의 표시 여부/순서를 관리하는 훅.
 * localStorage에 영속화되며, 레지스트리에 새 위젯이 추가되면 자동으로 반영된다.
 */
function useSidePanelWidgets() {
    const [prefs, setPrefs] = useState(loadPrefs);

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    }, [prefs]);

    const toggleWidget = useCallback((id) => {
        const widget = WIDGETS_BY_ID[id];
        if (!widget || widget.removable === false) return;
        setPrefs(prev => ({
            ...prev,
            enabled: { ...prev.enabled, [id]: !prev.enabled[id] },
        }));
    }, []);

    const moveWidget = useCallback((id, direction) => {
        setPrefs(prev => {
            const order = [...prev.order];
            const index = order.indexOf(id);
            const target = index + direction;
            if (index === -1 || target < 0 || target >= order.length) return prev;
            [order[index], order[target]] = [order[target], order[index]];
            return { ...prev, order };
        });
    }, []);

    const orderedWidgets = useMemo(() => {
        return prefs.order
            .map(id => WIDGETS_BY_ID[id])
            .filter(Boolean);
    }, [prefs.order]);

    return {
        orderedWidgets,
        enabled: prefs.enabled,
        toggleWidget,
        moveWidget,
    };
}

export default useSidePanelWidgets;
